import { useState, type FC, useEffect, type ReactNode } from 'react';
import { LeftSquareFilled, RightSquareFilled, EditOutlined, CloseOutlined } from '@ant-design/icons';
import { Button } from '@g2u/ui-kit';
import cx from 'classnames';
import { observer } from 'mobx-react-lite';

import { RootStore } from '../../../store';
import { type WithOptionalDataTest } from 'types';
import { AiAssistantContent } from './ai-assistant-content.component';
import { NotepadContent } from './notepad-content.component';
import styles from './side-panel.module.scss';

type Props = {
  isOpen?: boolean;
  placeholder?: string;
  children?: ReactNode;
} & WithOptionalDataTest;

export const SidePanel: FC<Props> = observer((props) => {
  const { isOpen = false, children, dataTestId } = props;

  const [opened, setOpened] = useState<boolean>(isOpen);
  const [isNotepadOpen, setIsNotepadOpen] = useState<boolean>(false);

  const { user } = RootStore;
  const isAiEnabled = !!user?.api_id;
  const placeholder = 'BMS Tools';

  useEffect(() => {
    setOpened(isOpen);
  }, [isOpen]);

  return (
    <div
      className={cx(styles.sidePanel, isAiEnabled && styles.sidePanel_aiEnabled, opened && styles.sidePanel_opened)}
      data-testid={dataTestId}
    >
      {placeholder && (
        <div
          className={cx(styles.sidePanel__placeholder, opened && styles.sidePanel__placeholder_hidden)}
          data-testid="side-panel-placeholder"
          onClick={() => {
            !opened && setOpened(!opened);
          }}
        >
          {placeholder}
        </div>
      )}
      <div
        className={styles.sidePanel__tag}
        data-testid="side-panel-tag"
        onClick={() => {
          setOpened(!opened);
        }}
      >
        {opened ? <RightSquareFilled /> : <LeftSquareFilled />}
      </div>

      <div className={styles.sidePanel__content}>
        {isAiEnabled && (
          <>
            <AiAssistantContent isOpen={opened} />
            {!isNotepadOpen && (
              <Button.$
                type={Button.Type.Default}
                dataTestId="notepad-button-open"
                icon={<EditOutlined />}
                onClick={() => setIsNotepadOpen(true)}
                className={styles.sidePanel__notepadButton}
              >
                Notepad
              </Button.$>
            )}
          </>
        )}
        {(!isAiEnabled || isNotepadOpen) && (
          <div className={cx(isAiEnabled ? styles.sidePanel__notepadOverlay : styles.sidePanel__notepadLegacy)}>
            {isNotepadOpen && (
              <div className={styles.sidePanel__notepadHeader}>
                <span>Notepad</span>
                <Button.$
                  type={Button.Type.Default}
                  dataTestId="notepad-button-close"
                  icon={<CloseOutlined />}
                  onClick={() => setIsNotepadOpen(false)}
                />
              </div>
            )}
            <div className={styles.sidePanel__notepadContent}>{children || <NotepadContent />}</div>
          </div>
        )}
      </div>
    </div>
  );
});
